/**
 * AI Chat Streaming Hook
 *
 * Encapsulates the SSE streaming logic for sending messages to the AI Chat
 * backend and receiving chunked responses with typing effect.
 */

import { useCallback, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useAIChatStore from '../stores/ai-chat-store';
import {
  AIChatMessage,
  AIChatCard,
  SendMessageInput,
  ChatHistoryResponse,
} from '../types/ai-chat.types';
import { refreshTokenCall, isTokenError } from '../API/APIs/APIv1';
import { API_BASE_URL } from './ai-chat-api';
import {
  AI_CHAT_QUERY_KEYS,
  DEFAULT_HISTORY_LIMIT,
  type StreamEvent,
  type ThinkingEvent,
  type ConversationsListResponse,
} from './ai-chat-store';

/** Cap for live thinking steps kept in memory per stream. */
const MAX_LIVE_THINKING_STEPS = 40;

/**
 * Return type for the streaming hook
 */
export interface UseAIChatStreamingReturn {
  isStreaming: boolean;
  streamingContent: string;
  streamingMessages: AIChatMessage[];
  latestSuggestions: string[];
  /** Live PDCA/tool progress events for the in-flight message. */
  liveThinking: ThinkingEvent[];
  send: (content: string, context?: SendMessageInput['context']) => Promise<{ message: AIChatMessage } | undefined>;
  cancelStreaming: () => void;
  addOptimisticMessage: (message: AIChatMessage) => void;
  removeOptimisticMessage: (messageId: string) => void;
}

/**
 * Hook that manages SSE streaming for AI Chat messages.
 *
 * Handles:
 * - Optimistic user message insertion
 * - SSE stream reading and chunk accumulation
 * - Query cache updates on stream completion
 * - 401 token refresh during streaming
 * - Abort controller for cancellation
 */
export function useAIChatStreaming(
  currentConversationId: string | null,
  setCurrentConversationId: (id: string | null) => void,
  historyData: ChatHistoryResponse | undefined,
): UseAIChatStreamingReturn {
  const queryClient = useQueryClient();

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingMessages, setStreamingMessages] = useState<AIChatMessage[]>([]);
  const [latestSuggestions, setLatestSuggestions] = useState<string[]>([]);
  const [liveThinking, setLiveThinking] = useState<ThinkingEvent[]>([]);
  const streamingMessagesRef = useRef<AIChatMessage[]>([]);
  const isStreamingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Optimistic cache updates
  // ─────────────────────────────────────────────────────────────────────────

  const addOptimisticMessage = useCallback(
    (message: AIChatMessage) => {
      queryClient.setQueryData<ChatHistoryResponse>(
        AI_CHAT_QUERY_KEYS.history(currentConversationId || undefined, DEFAULT_HISTORY_LIMIT),
        (old) => {
          if (!old) return { messages: [message], total: 1, limit: DEFAULT_HISTORY_LIMIT, offset: 0 };
          return {
            ...old,
            messages: [...old.messages, message],
            total: old.total + 1,
          };
        }
      );
    },
    [queryClient, currentConversationId]
  );

  const removeOptimisticMessage = useCallback(
    (messageId: string) => {
      queryClient.setQueryData<ChatHistoryResponse>(
        AI_CHAT_QUERY_KEYS.history(currentConversationId || undefined, DEFAULT_HISTORY_LIMIT),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((m) => m.id !== messageId),
            total: old.total - 1,
          };
        }
      );
    },
    [queryClient, currentConversationId]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Streaming send
  // ─────────────────────────────────────────────────────────────────────────

  const send = useCallback(
    async (content: string, context?: SendMessageInput['context']) => {
      if (isStreamingRef.current) return;

      // Create optimistic messages
      const optimisticUserId = `optimistic-user-${Date.now()}`;
      const optimisticAssistantId = `optimistic-assistant-${Date.now()}`;

      const userMessage: AIChatMessage = {
        id: optimisticUserId,
        role: 'user',
        content,
        timestamp: new Date(),
      };

      const assistantPlaceholder: AIChatMessage = {
        id: optimisticAssistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      // Initialize streaming messages with existing history + new messages
      const existingMessages = historyData?.messages ?? [];
      const initialMessages = [...existingMessages, userMessage, assistantPlaceholder];
      setStreamingMessages(initialMessages);
      streamingMessagesRef.current = initialMessages;

      // Also add to query cache
      addOptimisticMessage(userMessage);
      addOptimisticMessage(assistantPlaceholder);

      setIsStreaming(true);
      isStreamingRef.current = true;
      setStreamingContent('');
      setLatestSuggestions([]);
      setLiveThinking([]);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Helper to get current access token
        const getAccessToken = (): string => {
          const token = localStorage.getItem('user-token');
          const parsedToken = token ? JSON.parse(token) : null;
          if (!parsedToken?.accessToken) {
            throw new Error('No access token available');
          }
          return parsedToken.accessToken;
        };

        // Helper to make the streaming request
        const makeStreamRequest = async (accessToken: string) => {
          return fetch(`${API_BASE_URL}/ai-chat/message/stream`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ content, context, conversationId: currentConversationId }),
            signal: abortController.signal,
          });
        };

        let accessToken = getAccessToken();
        let response = await makeStreamRequest(accessToken);

        // Handle 401 with token refresh and retry
        if (response.status === 401) {
          const errorBody = await response.json().catch(() => ({ message: 'Unauthorized' }));
          const errorMessage = errorBody.message || 'Unauthorized';

          if (isTokenError(errorMessage)) {
            const refreshResult = await refreshTokenCall();
            if (refreshResult && !refreshResult.error && refreshResult.accessToken) {
              accessToken = refreshResult.accessToken;
              response = await makeStreamRequest(accessToken);
            } else {
              throw new Error('Session expired. Please log in again.');
            }
          } else {
            throw new Error(errorMessage);
          }
        }

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Failed to send message' }));
          throw new Error(error.message || 'Failed to send message');
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let streamedContent = '';
        let actualUserMessageId = optimisticUserId;
        let actualAssistantMessageId = optimisticAssistantId;
        let finalCards: AIChatCard[] | undefined;
        // Karten, die während des Streams kommen (z. B. Kontostatus bei der Anmeldung):
        // je Kartentyp und Konto nur die neueste.
        let liveCards: AIChatCard[] = [];
        const liveCardKey = (card: AIChatCard) => `${card.type}:${String((card.data as { accountId?: unknown } | undefined)?.accountId ?? '')}`;
        let finalExecutionMeta: StreamEvent['executionMeta'];

        let sseBuffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            try {
              const event: StreamEvent = JSON.parse(line.slice(6));

              if (event.type === 'start') {
                if (event.userMessageId) actualUserMessageId = event.userMessageId;
                if (event.messageId) actualAssistantMessageId = event.messageId;
              } else if (event.type === 'thinking' && event.thinking) {
                // Real orchestrator progress (plan → tool_call → observe → decide)
                const step = event.thinking;
                setLiveThinking((prev) => [...prev.slice(-(MAX_LIVE_THINKING_STEPS - 1)), step]);
              } else if (event.type === 'chunk' && event.cards?.length) {
                for (const card of event.cards) {
                  liveCards = [...liveCards.filter((c) => liveCardKey(c) !== liveCardKey(card)), card];
                }
                const updatedMessages = streamingMessagesRef.current.map((m) =>
                  m.id === optimisticAssistantId ? { ...m, cards: liveCards } : m
                );
                streamingMessagesRef.current = updatedMessages;
                setStreamingMessages(updatedMessages);
                if (event.content) {
                  streamedContent += event.content;
                  setStreamingContent(streamedContent);
                }
              } else if (event.type === 'chunk' && event.content) {
                streamedContent += event.content;
                setStreamingContent(streamedContent);

                const updatedMessages = streamingMessagesRef.current.map((m) =>
                  m.id === optimisticAssistantId ? { ...m, content: streamedContent } : m
                );
                streamingMessagesRef.current = updatedMessages;
                setStreamingMessages(updatedMessages);
              } else if (event.type === 'done') {
                finalCards = event.cards ?? (liveCards.length > 0 ? liveCards : undefined);
                finalExecutionMeta = event.executionMeta;
                setLatestSuggestions(event.suggestions || []);
                const targetConversationId = event.conversationId || currentConversationId;

                // Build final messages from ref (avoids stale closure)
                const finalMessages = streamingMessagesRef.current.map((m) => {
                  if (m.id === optimisticUserId) return { ...m, id: actualUserMessageId };
                  if (m.id === optimisticAssistantId) {
                    return {
                      ...m,
                      id: actualAssistantMessageId,
                      content: streamedContent,
                      cards: finalCards,
                      executionMeta: finalExecutionMeta,
                      isStreaming: false,
                    };
                  }
                  return m;
                });

                // Update cache before changing conversation ID
                queryClient.setQueryData<ChatHistoryResponse>(
                  AI_CHAT_QUERY_KEYS.history(targetConversationId || undefined, DEFAULT_HISTORY_LIMIT),
                  { messages: finalMessages, total: finalMessages.length, limit: DEFAULT_HISTORY_LIMIT, offset: 0 }
                );

                // Update conversation ID if this was a new conversation
                if (event.conversationId && event.conversationId !== currentConversationId) {
                  const wasNewChat = !currentConversationId;
                  setTimeout(() => {
                    setCurrentConversationId(event.conversationId!);
                    // Neuer Chat: die erste Nachricht lag im Speicher für „ohne ID“. Nach dem
                    // Wechsel leeren, sonst tauchte sie nach Löschen oder „Neuer Chat“ wieder auf.
                    if (wasNewChat) {
                      queryClient.removeQueries({ queryKey: AI_CHAT_QUERY_KEYS.history(undefined, DEFAULT_HISTORY_LIMIT), exact: true });
                    }
                  }, 0);
                }

                // Neue Unterhaltung: in der Liste steht sie noch nicht — neu laden, sonst
                // zeigte die Seitenleiste „Noch keine Unterhaltungen“ bis zum Seiten-Reload.
                const listed = queryClient
                  .getQueryData<ConversationsListResponse>([...AI_CHAT_QUERY_KEYS.conversations, 20])
                  ?.conversations.some((conv) => conv.id === (event.conversationId || targetConversationId));
                if (!listed) {
                  void queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
                }

                // Update conversations list timestamp without invalidating
                if (targetConversationId) {
                  queryClient.setQueryData<ConversationsListResponse>(
                    [...AI_CHAT_QUERY_KEYS.conversations, 20],
                    (old) => {
                      if (!old) return old;
                      const updated = old.conversations.map((conv) =>
                        conv.id === targetConversationId ? { ...conv, updatedAt: new Date() } : conv
                      );
                      updated.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
                      return { ...old, conversations: updated };
                    }
                  );
                }
              } else if (event.type === 'error') {
                throw new Error(event.content || 'An error occurred');
              }
            } catch {
              // Ignore parse errors for incomplete SSE chunks
            }
          }
        }

        // Process final buffered SSE line, if complete payload remains without trailing newline.
        if (sseBuffer.startsWith('data: ')) {
          try {
            const event: StreamEvent = JSON.parse(sseBuffer.slice(6));
            if (event.type === 'error') {
              throw new Error(event.content || 'An error occurred');
            }
          } catch {
            // Ignore trailing incomplete payload.
          }
        }

        return {
          message: {
            id: actualAssistantMessageId,
            role: 'assistant' as const,
            content: streamedContent,
            cards: finalCards,
            executionMeta: finalExecutionMeta,
            timestamp: new Date(),
          },
        };
      } catch (error: any) {
        removeOptimisticMessage(optimisticUserId);
        removeOptimisticMessage(optimisticAssistantId);
        setStreamingMessages([]);
        throw error;
      } finally {
        // Der Bot kann Daten geändert haben (Profil, Arbeitszeiten, ...) —
        // Formulare im Arbeitsbereich laden neu.
        useAIChatStore.getState().bumpDataVersion();
        setIsStreaming(false);
        isStreamingRef.current = false;
        setStreamingContent('');
        abortControllerRef.current = null;

        // Clear streaming messages after a short delay to prevent visual flash
        setTimeout(() => {
          if (!isStreamingRef.current) {
            streamingMessagesRef.current = [];
            setStreamingMessages([]);
          }
        }, 200);
      }
    },
    [
      addOptimisticMessage,
      removeOptimisticMessage,
      queryClient,
      currentConversationId,
      setCurrentConversationId,
      historyData,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Cancel
  // ─────────────────────────────────────────────────────────────────────────

  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    isStreaming,
    streamingContent,
    streamingMessages,
    latestSuggestions,
    liveThinking,
    send,
    cancelStreaming,
    addOptimisticMessage,
    removeOptimisticMessage,
  };
}
